export const DEV_DISPLAY_LANGUAGES =
  typeof __DEV__ !== 'undefined' && __DEV__ ? (['en-XA', 'en-XB'] as const) : ([] as const)

export const DEV_LANGUAGE_NAMES = Object.freeze(
  typeof __DEV__ !== 'undefined' && __DEV__
    ? {
        'en-XA': { name: '(DEV) English (A)' },
        'en-XB': { name: '(DEV) English (B)' },
        'xx-LS': { name: '(DEV) English (Long)' },
        'xx-AC': { name: '(DEV) English (All Caps)' },
      }
    : {},
)

// Languages that when selected take the user to the Bukwild microsites
export const RIGHT_TO_LEFT_LANGUAGES = Object.freeze({
  ar: { name: 'العربية', micrositePath: 'arabic' }, // Arabic
  'he-IL': { name: 'עברית', micrositePath: 'hebrew' }, // Hebrew
  'ur-PK': { name: 'اردو', micrositePath: 'urdu' }, // Urdu
})

/**
 * All supported display languages
 *
 * Keys are IETF BCP 47 language tag.
 *
 * `name`: A name for display purposes in its own language.
 */
export const LYRA_DISPLAY_LANGUAGES = Object.freeze({
  'bg-BG': { name: 'български' }, // Bulgarian
  'cs-CZ': { name: 'Čeština' }, // Czech
  'da-DK': { name: 'Dansk' }, // Danish
  'de-DE': { name: 'Deutsch' }, // German
  'el-GR': { name: 'Ελληνικά' }, // Greek
  'en-US': { name: 'English' }, // English
  'en-GB': { name: 'English (UK)' }, // English (UK)
  'es-419': { name: 'Español' }, // Spanish (Latin America)
  'es-ES': { name: 'Español (España)' }, // Spanish (Spain)
  fil: { name: 'Filipino' }, // Filipino
  'fi-FI': { name: 'Suomi' }, // Finnish
  'fr-CA': { name: 'Français (Canada)' }, // French (Canada)
  'fr-FR': { name: 'Français (France)' }, // French (France)
  'hi-IN': { name: 'हिन्दी' }, // Hindi
  'hu-HU': { name: 'Magyar' }, // Hungarian
  'id-ID': { name: 'Bahasa Indonesia' }, // Indonesian
  'it-IT': { name: 'Italiano' }, // Italian
  'ja-JP': { name: '日本語' }, // Japanese
  'ko-KR': { name: '한국어' }, // Korean
  'ms-MY': { name: 'Bahasa Melayu' }, // Malay
  'nb-NO': { name: 'Norsk (bokmål)' }, // Norwegian
  'nl-NL': { name: 'Nederlands' }, // Dutch
  'pl-PL': { name: 'Polski' }, // Polish
  'pt-BR': { name: 'Português (Brasil)' }, // Portuguese (Brazil)
  'pt-PT': { name: 'Português (Portugal)' }, // Portuguese (Portugal)
  'ro-RO': { name: 'Română' }, // Romanian
  'sv-SE': { name: 'Svenska' }, // Swedish
  'th-TH': { name: 'ภาษาไทย' }, // Thai
  'tr-TR': { name: 'Türkçe' }, // Turkish
  'vi-VN': { name: 'Tiếng Việt' }, // Vietnamese
  'zh-CN': { name: '中文(简体)' }, // Simplified Chinese (China)
  'zh-HK': { name: '中文(香港)' }, // Traditional Chinese (Hong Kong)
  'zh-TW': { name: '中文(台灣)' }, // Traditional Chinese (Taiwan)
  ...RIGHT_TO_LEFT_LANGUAGES,
  ...DEV_LANGUAGE_NAMES,
})
export const DEFAULT_LANGUAGE = 'en-US'

/**
 * Languages enabled when feature flag is not in use
 *
 * Currently includes all languages up to the Jan 2024 intl release
 */
export const LANGUAGES_ENABLED = [
  'bg-BG',
  'cs-CZ',
  'de-DE',
  'en-US',
  'es-419',
  'es-ES',
  'fil',
  'fr-CA',
  'fr-FR',
  'hi-IN',
  'hu-HU',
  'id-ID',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'ms-MY',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ro-RO',
  'th-TH',
  'tr-TR',
  'vi-VN',
  'zh-CN',
  'zh-HK',
  'zh-TW',
] as const

/**
 * Type contains all known Lyra language tags including dev languages
 */
export type LyraDisplayLanguageTags = keyof typeof LYRA_DISPLAY_LANGUAGES

export const LANG_QUERY_PARAM = 'lang'

export interface CountryNameToISOCodeObject {
  [countryName: string]: string
}

export const CountryNameToISOCode: CountryNameToISOCodeObject = {
  Afghanistan: 'AF',
  AlandIslands: 'AX',
  Albania: 'AL',
  Algeria: 'DZ',
  AmericanSamoa: 'AS',
  Andorra: 'AD',
  Angola: 'AO',
  Anguilla: 'AI',
  Antarctica: 'AQ',
  AntiguaAndBarbuda: 'AG',
  Argentina: 'AR',
  Armenia: 'AM',
  Aruba: 'AW',
  Australia: 'AU',
  Austria: 'AT',
  Azerbaijan: 'AZ',
  Bahamas: 'BS',
  Bahrain: 'BH',
  Bangladesh: 'BD',
  Barbados: 'BB',
  Belarus: 'BY',
  Belgium: 'BE',
  Belize: 'BZ',
  Benin: 'BJ',
  Bermuda: 'BM',
  Bhutan: 'BT',
  Bolivia: 'BO',
  BonaireSintEustatiusAndSaba: 'BQ',
  BosniaAndHerzegovina: 'BA',
  Botswana: 'BW',
  BouvetIsland: 'BV',
  Brazil: 'BR',
  BritishIndianOceanTerritory: 'IO',
  BruneiDarussalam: 'BN',
  Bulgaria: 'BG',
  BurkinaFaso: 'BF',
  Burundi: 'BI',
  CaboVerde: 'CV',
  Cambodia: 'KH',
  Cameroon: 'CM',
  Canada: 'CA',
  CaymanIslands: 'KY',
  CentralAfricanRepublic: 'CF',
  Chad: 'TD',
  Chile: 'CL',
  China: 'CN',
  ChristmasIsland: 'CX',
  CocosKeelingIslands: 'CC',
  Colombia: 'CO',
  Comoros: 'KM',
  CookIslands: 'CK',
  CostaRica: 'CR',
  CoteDIvoire: 'CI',
  Croatia: 'HR',
  Cuba: 'CU',
  Curacao: 'CW',
  Cyprus: 'CY',
  CzechRepublic: 'CZ',
  DemocraticRepublicOfTheCongo: 'CD',
  Denmark: 'DK',
  Djibouti: 'DJ',
  Dominica: 'DM',
  DominicanRepublic: 'DO',
  Ecuador: 'EC',
  Egypt: 'EG',
  ElSalvador: 'SV',
  EquatorialGuinea: 'GQ',
  Eritrea: 'ER',
  Estonia: 'EE',
  Eswatini: 'SZ',
  Ethiopia: 'ET',
  FalklandIslands: 'FK',
  FaroeIslands: 'FO',
  Fiji: 'FJ',
  Finland: 'FI',
  France: 'FR',
  FrenchGuiana: 'GF',
  FrenchPolynesia: 'PF',
  FrenchSouthernTerritories: 'TF',
  Gabon: 'GA',
  Gambia: 'GM',
  Georgia: 'GE',
  Germany: 'DE',
  Ghana: 'GH',
  Gibraltar: 'GI',
  Greece: 'GR',
  Greenland: 'GL',
  Grenada: 'GD',
  Guadeloupe: 'GP',
  Guam: 'GU',
  Guatemala: 'GT',
  Guernsey: 'GG',
  Guinea: 'GN',
  GuineaBissau: 'GW',
  Guyana: 'GY',
  Haiti: 'HT',
  HeardIslandAndMcDonaldIslands: 'HM',
  HolySee: 'VA',
  Honduras: 'HN',
  HongKong: 'HK',
  Hungary: 'HU',
  Iceland: 'IS',
  India: 'IN',
  Indonesia: 'ID',
  Iran: 'IR',
  Iraq: 'IQ',
  Ireland: 'IE',
  IsleOfMan: 'IM',
  Israel: 'IL',
  Italy: 'IT',
  Jamaica: 'JM',
  Japan: 'JP',
  Jersey: 'JE',
  Jordan: 'JO',
  Kazakhstan: 'KZ',
  Kenya: 'KE',
  Kiribati: 'KI',
  Kuwait: 'KW',
  Kyrgyzstan: 'KG',
  Laos: 'LA',
  Latvia: 'LV',
  Lebanon: 'LB',
  Lesotho: 'LS',
  Liberia: 'LR',
  Libya: 'LY',
  Liechtenstein: 'LI',
  Lithuania: 'LT',
  Luxembourg: 'LU',
  Macao: 'MO',
  Madagascar: 'MG',
  Malawi: 'MW',
  Malaysia: 'MY',
  Maldives: 'MV',
  Mali: 'ML',
  Malta: 'MT',
  MarshallIslands: 'MH',
  Martinique: 'MQ',
  Mauritania: 'MR',
  Mauritius: 'MU',
  Mayotte: 'YT',
  Mexico: 'MX',
  Micronesia: 'FM',
  Moldova: 'MD',
  Monaco: 'MC',
  Mongolia: 'MN',
  Montenegro: 'ME',
  Montserrat: 'MS',
  Morocco: 'MA',
  Mozambique: 'MZ',
  Myanmar: 'MM',
  Namibia: 'NA',
  Nauru: 'NR',
  Nepal: 'NP',
  Netherlands: 'NL',
  NewCaledonia: 'NC',
  NewZealand: 'NZ',
  Nicaragua: 'NI',
  Niger: 'NE',
  Nigeria: 'NG',
  Niue: 'NU',
  NorfolkIsland: 'NF',
  NorthKorea: 'KP',
  NorthMacedonia: 'MK',
  NorthernMarianaIslands: 'MP',
  Norway: 'NO',
  Oman: 'OM',
  Pakistan: 'PK',
  Palau: 'PW',
  Palestine: 'PS',
  Panama: 'PA',
  PapuaNewGuinea: 'PG',
  Paraguay: 'PY',
  Peru: 'PE',
  Philippines: 'PH',
  Pitcairn: 'PN',
  Poland: 'PL',
  Portugal: 'PT',
  PuertoRico: 'PR',
  Qatar: 'QA',
  RepublicOfKorea: 'KR',
  RepublicOfTheCongo: 'CG',
  Reunion: 'RE',
  Romania: 'RO',
  Russia: 'RU',
  Rwanda: 'RW',
  SaintBarthelemy: 'BL',
  SaintHelenaAscensionAndTristanDaCunha: 'SH',
  SaintKittsAndNevis: 'KN',
  SaintLucia: 'LC',
  SaintMartin: 'MF',
  SaintPierreAndMiquelon: 'PM',
  SaintVincentAndTheGrenadines: 'VC',
  Samoa: 'WS',
  SanMarino: 'SM',
  SaoTomeAndPrincipe: 'ST',
  SaudiArabia: 'SA',
  Senegal: 'SN',
  Serbia: 'RS',
  Seychelles: 'SC',
  SierraLeone: 'SL',
  Singapore: 'SG',
  SintMaarten: 'SX',
  Slovakia: 'SK',
  Slovenia: 'SI',
  SolomonIslands: 'SB',
  Somalia: 'SO',
  SouthAfrica: 'ZA',
  SouthGeorgiaAndTheSouthSandwichIslands: 'GS',
  SouthSudan: 'SS',
  Spain: 'ES',
  SriLanka: 'LK',
  Sudan: 'SD',
  Suriname: 'SR',
  SvalbardAndJanMayen: 'SJ',
  Sweden: 'SE',
  Switzerland: 'CH',
  Syria: 'SY',
  Taiwan: 'TW',
  Tajikistan: 'TJ',
  Tanzania: 'TZ',
  Thailand: 'TH',
  TimorLeste: 'TL',
  Togo: 'TG',
  Tokelau: 'TK',
  Tonga: 'TO',
  TrinidadAndTobago: 'TT',
  Tunisia: 'TN',
  Türkiye: 'TR',
  Turkmenistan: 'TM',
  TurksAndCaicosIslands: 'TC',
  Tuvalu: 'TV',
  Uganda: 'UG',
  Ukraine: 'UA',
  UnitedArabEmirates: 'AE',
  UnitedKingdom: 'GB',
  UnitedStatesMinorOutlyingIslands: 'UM',
  UnitedStatesOfAmerica: 'US',
  Uruguay: 'UY',
  Uzbekistan: 'UZ',
  Vanuatu: 'VU',
  Venezuela: 'VE',
  VietNam: 'VN',
  VirginIslandsBritish: 'VG',
  VirginIslandsUS: 'VI',
  WallisAndFutuna: 'WF',
  WesternSahara: 'EH',
  Yemen: 'YE',
  Zambia: 'ZM',
  Zimbabwe: 'ZW',
  Kosovo: 'XK',
}
